import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Loader2, Pencil, Star } from 'lucide-react';
import { api } from '../lib/api';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { PrioCriteriaPicker, computePrioWith, usePrioConfig, PRIO_LABEL } from './PrioCriteriaPicker';

// Max (22.10.2026): every non-AC team member can set or correct a deal's prio from the deal header —
// e.g. assign older (legacy) deals to the new category C so they run in the Prio C sequence.
export function PrioEditButton({ project, onSaved }) {
  const [open, setOpen] = useState(false);
  const has = !!project?.prio;
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="prio-edit-btn"
        title={has ? 'Prio korrigieren' : 'Prio setzen (Kriterien ankreuzen)'}
        className={`inline-flex items-center gap-1 text-[9px] font-mono rounded px-1.5 py-0.5 border transition-colors shrink-0 ${has
          ? 'text-muted-foreground/60 border-transparent hover:text-foreground hover:border-border'
          : 'text-amber-300/80 border-dashed border-amber-500/40 hover:bg-amber-500/10'}`}>
        {has ? <Pencil className="w-3 h-3" /> : <><Star className="w-3 h-3" /> Prio setzen</>}
      </button>
      {open && <PrioEditDialog project={project} onClose={() => setOpen(false)} onSaved={onSaved} />}
    </>
  );
}

function PrioEditDialog({ project, onClose, onSaved }) {
  const cfg = usePrioConfig();
  const [ids, setIds] = useState(project.prio_criteria || []);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setIds(project.prio_criteria || []); }, [project.id, project.prio_criteria]);
  const picked = computePrioWith(ids, cfg?.rules, cfg?.criteria);
  const isPass = picked === 'PASS';
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.setProjectPrio(project.id, ids);
      toast.success(`Prio ${r.data.prio} gesetzt${project.prio && project.prio !== r.data.prio ? ` (vorher ${project.prio})` : ''} — Sequenz & Rollen folgen der neuen Prio`);
      onClose(); onSaved && onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || 'Prio konnte nicht gesetzt werden'); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-card border-border rounded-sm max-w-lg max-h-[90vh] overflow-y-auto" data-testid="prio-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg text-foreground flex items-center gap-2">
            <Star className="w-5 h-5 text-amber-400" /> {project.prio ? `Prio korrigieren (aktuell Prio ${project.prio})` : 'Prio setzen'}
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            Kriterien ankreuzen — die Prio ergibt sich aus den Admin-Regeln. Sie bestimmt Brief-Schreiber und die Outreach-Sequenz
            (ein nachträglich gesetztes Prio schaltet auch Alt-Deals auf die Prio-Sequenz).
          </DialogDescription>
        </DialogHeader>
        <PrioCriteriaPicker value={ids} onChange={setIds} />
        {isPass && (
          <p className="text-[11px] text-amber-300" data-testid="prio-edit-pass-hint">
            Diese Auswahl ergibt keine Prio, sondern einen Pass (Bench) — dafür bitte den Deal über „Pass“ ablegen.
          </p>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="prio-edit-cancel-btn">Abbrechen</Button>
          <Button onClick={save} disabled={busy || isPass || !ids.length} data-testid="prio-edit-save-btn"
            className="bg-amber-600 hover:bg-amber-500 text-white rounded-sm text-xs h-8">
            {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null} Speichern als {PRIO_LABEL(picked)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
